import {
  BtnLanguage,
  LanguageBox,
  LanguageListBox,
  PolicyArticleTitle,
  PolicyContainer,
  PolicyHyphenList,
  PolicyList,
  PolicySectionTitle,
  PolicyTable,
  PolicyText,
  PolicyTitle,
  PolicyVersion,
  StyledIconDropdown,
  StyledIconGlobal,
} from "styles/PolicyCommon";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const PrivacyEn = () => {
  const [isOpen, setIsOpen] = useState(false);
  const onLanguageClick = () => setIsOpen((prev) => !prev);

  useEffect(() => {
    document.documentElement.lang = "en";
    document.title = "pregnancy&baby";
    return () => {
      document.documentElement.lang = "en";
      document.title = "pregnancy&baby";
    };
  }, []);

  return (
    <PolicyContainer>
      <h1 className="hide">pregnancy&amp;baby</h1>
      <LanguageBox isOpen={isOpen}>
        <BtnLanguage onClick={onLanguageClick}>
          <StyledIconGlobal />
          EN
          <StyledIconDropdown />
        </BtnLanguage>
        <LanguageListBox>
          <ul>
            <li className="selected">
              <em>EN</em>
            </li>
            <li>
              <Link to="/policy/ja/privacy">JA</Link>
            </li>
            <li>
              <Link to="/policy/vi/privacy">VI</Link>
            </li>
            <li>
              <Link to="/policy/th/privacy">TH</Link>
            </li>
            <li>
              <Link to="/policy/id/privacy">ID</Link>
            </li>
            <li>
              <Link to="/policy/es/privacy">ES</Link>
            </li>
          </ul>
        </LanguageListBox>
      </LanguageBox>

      <PolicyTitle>Privacy Policy</PolicyTitle>

      <PolicyVersion>
        Effective Date: [September 21, 2026]
        <br />
        Last Updated: [August 25, 2026]
      </PolicyVersion>
      <PolicyText>
        Connecti Co., Ltd. (“Connecti,” “we,” “us,” or “our”) values the privacy
        of users of <strong>pregnancy&amp;baby</strong> and is committed to
        handling Personal Data transparently and securely.
      </PolicyText>
      <PolicyText>
        This Privacy Policy explains what Personal Data we collect, how and why
        we use it, how it may be shared or transferred, how long we retain it,
        and the rights available to you.
      </PolicyText>
      <PolicyText>
        This Privacy Policy applies to the <strong>pregnancy&amp;baby</strong>{" "}
        mobile application, website, and related products and services
        (collectively, the “Services”).
      </PolicyText>
      <PolicyText>
        We may update this Privacy Policy from time to time. If we make material
        changes, we will provide notice through the Services, by email, or by
        other appropriate means as required by applicable law. Where required by
        law, we will obtain your consent to material changes.
      </PolicyText>

      <PolicySectionTitle>1. Personal Data We Collect</PolicySectionTitle>
      <PolicyText>
        We may collect Personal Data that you provide directly to us,
        information collected automatically when you use the Services, and
        certain Personal Data received from third-party authentication
        providers.
      </PolicyText>

      <PolicyArticleTitle>
        1.1 Account and Profile Information
      </PolicyArticleTitle>
      <PolicyText>
        When you create or use an account, we may collect:
      </PolicyText>
      <PolicyHyphenList>
        <li>Email address</li>
        <li>Year of birth</li>
        <li>Nickname</li>
        <li>Profile photo</li>
        <li>Place of residence</li>
        <li>Language</li>
        <li>Time zone</li>
      </PolicyHyphenList>
      <PolicyText>
        The specific information collected may vary depending on the features
        you use and the method you choose to create your account.
      </PolicyText>

      <PolicyArticleTitle>1.2 Social Login Information</PolicyArticleTitle>
      <PolicyText>You may create or access your account using:</PolicyText>
      <PolicyHyphenList>
        <li>Google</li>
        <li>Apple</li>
      </PolicyHyphenList>
      <PolicyText>
        When you use a third-party login service, we may receive information
        made available by the relevant provider, including:
      </PolicyText>
      <PolicyHyphenList>
        <li>Account identifier (ID)</li>
        <li>Name</li>
      </PolicyHyphenList>
      <PolicyText>
        The information we receive may vary depending on the provider and your
        settings with that provider.
      </PolicyText>
      <PolicyText>
        Each third-party login provider processes your information in accordance
        with its own privacy policy.
      </PolicyText>

      <PolicyArticleTitle>
        1.3 Health, Fertility, Pregnancy, and Well-being Information
      </PolicyArticleTitle>
      <PolicyText>
        Certain features of the Services allow you to voluntarily provide
        information relating to your health, fertility, pregnancy, and
        well-being.
      </PolicyText>
      <PolicyText>
        Depending on the features you use, this information may include:
      </PolicyText>

      <PolicyList>
        <li>
          <strong>Pregnancy Preparation</strong>
          <PolicyHyphenList>
            <li>Menstrual cycle and menstrual period information</li>
            <li>Last menstrual period</li>
            <li>Menstrual start and end dates</li>
            <li>Ovulation date</li>
            <li>Ovulation test date and time</li>
            <li>Ovulation test images and results</li>
            <li>Pregnancy test date and time</li>
            <li>Pregnancy test images and results</li>
            <li>Basal body temperature</li>
            <li>Symptoms</li>
            <li>Bleeding and pain records</li>
            <li>Medication records</li>
            <li>Vaccination records</li>
            <li>Medical appointment records</li>
            <li>Sexual activity records</li>
            <li>Notes and other diary information</li>
          </PolicyHyphenList>
        </li>
        <li>
          <strong>Fertility Treatment</strong>
          <PolicyHyphenList>
            <li>Type of fertility treatment</li>
            <li>Treatment cycle</li>
            <li>Start and end dates of fertility treatment</li>
            <li>Medication and injection records</li>
            <li>Treatment schedules and details</li>
          </PolicyHyphenList>
        </li>
        <li>
          <strong>Pregnancy</strong>
          <PolicyHyphenList>
            <li>Estimated due date</li>
            <li>Baby nickname</li>
            <li>Gestational age</li>
            <li>Pregnancy diary information</li>
            <li>Medical appointment records</li>
            <li>Vaccination records</li>
            <li>Weight records</li>
            <li>Ultrasound images</li>
            <li>Fetal measurements, including EFW, AC, FL, BPD, HC, and CRL</li>
            <li>Pre-pregnancy height and weight</li>
            <li>Weight history</li>
            <li>Other pregnancy-related information you choose to record</li>
          </PolicyHyphenList>
        </li>
        <li>
          <strong>Child Care and Postpartum</strong>
          <br />
          Depending on the features you use, we may collect information relating
          to your child, including:
          <PolicyHyphenList>
            <li>Child's name</li>
            <li>Sex</li>
            <li>Date of birth</li>
            <li>Birth weight</li>
            <li>Birth height</li>
            <li>Head circumference at birth</li>
            <li>Estimated due date</li>
            <li>Health screening records</li>
            <li>Vaccination records</li>
            <li>
              Growth measurements, including height, weight, and head
              circumference
            </li>
            <li>Developmental assessment records and scores</li>
            <li>Development-related photos</li>
            <li>Premature birth information</li>
            <li>Body temperature records</li>
            <li>Medication records</li>
            <li>Illness records</li>
          </PolicyHyphenList>
          We may also collect information relating to postpartum weight
          management, including:
          <PolicyHyphenList>
            <li>Pre-pregnancy height and weight</li>
            <li>Weight immediately before childbirth</li>
            <li>Postpartum weight records</li>
          </PolicyHyphenList>
          Some of this information may constitute health data, sensitive
          Personal Data, or special categories of Personal Data under applicable
          privacy laws.
          <br />
          Where required by applicable law, we will obtain your explicit or
          separate consent before collecting or processing such information.
        </li>
      </PolicyList>

      <PolicyArticleTitle>1.4 Community Information</PolicyArticleTitle>
      <PolicyText>
        When you use community features within{" "}
        <strong>pregnancy&amp;baby</strong>, we may process:
      </PolicyText>
      <PolicyHyphenList>
        <li>Nickname</li>
        <li>Profile photo</li>
        <li>Posts</li>
        <li>Comments</li>
        <li>Polls and poll responses</li>
        <li>Images or other content you voluntarily submit</li>
      </PolicyHyphenList>
      <PolicyText>
        Information you post in community areas may be visible to other users.
        Please carefully consider what information you choose to make public.
      </PolicyText>

      <PolicyArticleTitle>
        1.5 Information Collected Automatically
      </PolicyArticleTitle>
      <PolicyText>
        When you use the Services, certain technical and usage information may
        be collected automatically, including:
      </PolicyText>
      <PolicyHyphenList>
        <li>Device model</li>
        <li>Operating system and version</li>
        <li>App version</li>
        <li>Language</li>
        <li>Time zone</li>
        <li>IP address</li>
        <li>App usage and activity information</li>
        <li>Features accessed or used</li>
        <li>Frequency of use</li>
        <li>Interactions with particular features</li>
        <li>Error and diagnostic information</li>
        <li>FCM registration token or other push notification token</li>
      </PolicyHyphenList>
      <PolicyText>
        We may use cookies and similar technologies on our websites where
        applicable.
      </PolicyText>

      <PolicyArticleTitle>1.6 Analytics Information</PolicyArticleTitle>
      <PolicyText>
        We use <strong>Google Analytics for Firebase</strong> to understand how
        users interact with the Services and to improve functionality and user
        experience.
      </PolicyText>
      <PolicyText>Analytics information may include:</PolicyText>
      <PolicyHyphenList>
        <li>App usage information</li>
        <li>Session information</li>
        <li>Feature usage and interaction information</li>
        <li>Device and operating system information</li>
        <li>General technical and diagnostic information</li>
      </PolicyHyphenList>

      <PolicySectionTitle>
        2. How and Why We Use Personal Data
      </PolicySectionTitle>
      <PolicyText>
        We process Personal Data only for specified and legitimate purposes and,
        where required, on an appropriate legal basis.
      </PolicyText>
      <PolicyText>
        Depending on applicable law, the legal basis for processing may include
        your consent, performance of a contract with you, compliance with legal
        obligations, or our legitimate interests.
      </PolicyText>
      <PolicyText>We may use your Personal Data to:</PolicyText>
      <PolicyHyphenList>
        <li>Create, authenticate, and manage your account</li>
        <li>Provide and operate the Services</li>
        <li>
          Predict menstrual periods, fertile windows, and estimated ovulation
          dates
        </li>
        <li>Record and analyze ovulation and pregnancy test results</li>
        <li>Manage fertility treatment schedules and reminders</li>
        <li>Calculate gestational age and estimated due dates</li>
        <li>Provide pregnancy-related content and reports</li>
        <li>Analyze fetal growth measurements</li>
        <li>Provide pregnancy and postpartum weight-management features</li>
        <li>Provide child growth and developmental reports</li>
        <li>
          Provide child health, body temperature, medication, vaccination, and
          health screening management features
        </li>
        <li>
          Personalize content and Services based on information you provide
        </li>
        <li>Provide reminders and push notifications</li>
        <li>Respond to customer support inquiries</li>
        <li>Maintain the security and integrity of the Services</li>
        <li>Detect and prevent abuse or unauthorized activity</li>
        <li>Monitor application performance and errors</li>
        <li>Analyze how the Services are used</li>
        <li>Improve existing Services and develop new features</li>
        <li>
          Conduct statistical and scientific research using anonymized data
        </li>
        <li>
          Comply with applicable laws and lawful requests from authorities
        </li>
      </PolicyHyphenList>
      <PolicyText>We do not sell or rent your Personal Data.</PolicyText>

      <PolicySectionTitle>
        3. Health and Sensitive Personal Data
      </PolicySectionTitle>
      <PolicyText>
        <strong>pregnancy&amp;baby</strong> provides features relating to
        fertility, menstruation, pregnancy, maternal health, and child care. As
        a result, some of the Personal Data you provide may be considered
        sensitive Personal Data, health data, or special categories of Personal
        Data under applicable privacy laws.
      </PolicyText>
      <PolicyText>
        We process such information only to provide the features you choose to
        use, for other purposes disclosed to you, or as otherwise permitted by
        applicable law.
      </PolicyText>
      <PolicyText>
        Where applicable law requires explicit consent for the processing of
        health or other sensitive Personal Data, we will obtain such consent
        separately.
      </PolicyText>
      <PolicyText>We do not sell health or sensitive Personal Data.</PolicyText>

      <PolicySectionTitle>
        4. Automated Analysis and Personalized Services
      </PolicySectionTitle>
      <PolicyText>
        Certain features of <strong>pregnancy&amp;baby</strong> analyze
        information you provide in order to generate personalized results.
      </PolicyText>
      <PolicyText>For example, we may use your data to:</PolicyText>
      <PolicyHyphenList>
        <li>Estimate future menstrual periods and fertile windows</li>
        <li>Estimate ovulation dates</li>
        <li>Analyze ovulation or pregnancy test images</li>
        <li>Calculate pregnancy progress</li>
        <li>Analyze fetal growth measurements</li>
        <li>Analyze maternal weight trends</li>
        <li>Analyze child growth and development</li>
      </PolicyHyphenList>
      <PolicyText>
        These analyses are intended to provide informational and supportive
        features within the Services and are not intended to replace
        professional medical diagnosis, treatment, or medical advice.
      </PolicyText>

      <PolicySectionTitle>
        5. Push Notifications and Communications
      </PolicySectionTitle>
      <PolicyText>
        With your consent, we may send push notifications relating to:
      </PolicyText>
      <PolicyHyphenList>
        <li>Service reminders</li>
        <li>Diary and health-record reminders</li>
        <li>Pregnancy or child-care information</li>
        <li>Service updates</li>
        <li>
          Events, promotions, or other information where you have separately
          agreed to receive such communications
        </li>
      </PolicyHyphenList>
      <PolicyText>
        To deliver push notifications, we process an FCM registration token or
        an equivalent push notification token.
      </PolicyText>
      <PolicyText>
        You may withdraw your consent to push notifications at any time through
        the Services or your device settings.
      </PolicyText>
      <PolicyText>
        Withdrawing consent to optional notifications does not prevent us from
        providing essential service-related notices where permitted by
        applicable law.
      </PolicyText>

      <PolicySectionTitle>
        6. Cookies and Similar Technologies
      </PolicySectionTitle>
      <PolicyText>
        Where applicable, our Website may use cookies or similar technologies to
        operate the Services, remember settings, understand usage, and improve
        user experience.
      </PolicyText>
      <PolicyText>
        You may control cookies through your browser settings. Disabling certain
        cookies may affect the operation of some Website features.
      </PolicyText>

      <PolicySectionTitle>7. Third-Party Service Providers</PolicySectionTitle>
      <PolicyText>
        We use third-party service providers to operate, maintain, analyze, and
        improve the Services.
      </PolicyText>
      <PolicyText>Our principal service providers include:</PolicyText>
      <PolicyTable>
        <caption className="hide">
          Type, processor, Processor’s privacy, Data collected, Purpose
        </caption>
        <colgroup>
          <col style={{ width: "20%" }} />
          <col style={{ width: "20%" }} />
          <col />
          <col style={{ width: "25%" }} />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">Category</th>
            <th scope="col">Service Provider</th>
            <th scope="col">Data or Processing</th>
            <th scope="col">Purpose</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Infrastructure and data storage</td>
            <td>Amazon Web Services (AWS)</td>
            <td className="cell_left">
              Personal Data processed through the Services
            </td>
            <td className="cell_left">Hosting and data storage</td>
          </tr>
          <tr>
            <td>Communications</td>
            <td>NHN Cloud</td>
            <td className="cell_left">
              Contact and communication-related information
            </td>
            <td className="cell_left">Messages and service communications</td>
          </tr>
          <tr>
            <td>Analytics</td>
            <td>Google Analytics for Firebase</td>
            <td className="cell_left">App usage and analytics information</td>
            <td className="cell_left">Service analytics and improvement</td>
          </tr>
          <tr>
            <td>Image processing</td>
            <td>Google Cloud Vision</td>
            <td className="cell_left">
              Images submitted to applicable image-analysis features
            </td>
            <td className="cell_left">Image analysis and functionality</td>
          </tr>
          <tr>
            <td>Application monitoring</td>
            <td>Sentry</td>
            <td className="cell_left">
              Application logs, error and diagnostic information
            </td>
            <td className="cell_left">
              Performance monitoring and error tracking
            </td>
          </tr>
        </tbody>
      </PolicyTable>
      <PolicyText>
        Service providers may process Personal Data only to the extent necessary
        to provide services to us and are required to handle such information in
        accordance with applicable contractual and legal obligations.
      </PolicyText>
      <PolicyText>
        Our service providers may change as the Services evolve.
      </PolicyText>

      <PolicySectionTitle>
        8. When We May Share Personal Data
      </PolicySectionTitle>
      <PolicyText>We do not sell or rent your Personal Data.</PolicyText>
      <PolicyText>
        We may disclose or share Personal Data only in limited circumstances,
        including:
      </PolicyText>
      <PolicyHyphenList>
        <li>With service providers acting on our behalf</li>
        <li>When you direct or consent to the disclosure</li>
        <li>
          When required by applicable law, regulation, court order, or lawful
          governmental request
        </li>
        <li>
          When reasonably necessary to protect the rights, safety, or security
          of you, other users, Connecti, or the Services
        </li>
        <li>
          In connection with a merger, acquisition, restructuring, financing,
          sale of assets, or similar corporate transaction, subject to
          applicable law
        </li>
      </PolicyHyphenList>
      <PolicyText>
        We may also use or share information that has been irreversibly
        anonymized so that it can no longer identify an individual.
      </PolicyText>

      <PolicySectionTitle>9. Anonymized Data and Research</PolicySectionTitle>
      <PolicyText>
        We may anonymize certain data generated through your use of the Services
        for statistical, analytical, scientific, and research purposes.
      </PolicyText>
      <PolicyText>
        This may include research relating to women's health, fertility,
        pregnancy, maternal health, and child growth and development.
      </PolicyText>
      <PolicyText>Our anonymization process is designed so that:</PolicyText>
      <PolicyHyphenList>
        <li>The data can no longer reasonably identify you;</li>
        <li>Identifiers linking the data to your account are removed;</li>
        <li>
          The anonymized data cannot be linked back to your account or Personal
          Data; and
        </li>
        <li>We cannot re-identify you from the anonymized data.</li>
      </PolicyHyphenList>
      <PolicyText>
        Once information has been irreversibly anonymized so that it can no
        longer identify an individual, it is no longer treated as Personal Data
        to the extent provided by applicable law.
      </PolicyText>
      <PolicyText>
        <strong>Anonymized data may be retained indefinitely</strong> for
        research, statistical, analytical, and service-improvement purposes.
      </PolicyText>
      <PolicyText>
        We may use or share anonymized or aggregated research results with
        research institutions or other partners, provided that such information
        cannot reasonably be used to identify you.
      </PolicyText>

      <PolicySectionTitle>
        10. Data Retention and Account Deletion
      </PolicySectionTitle>
      <PolicyText>
        We retain identifiable Personal Data only for as long as reasonably
        necessary to provide the Services, fulfill the purposes described in
        this Privacy Policy, or comply with applicable legal obligations.
      </PolicyText>
      <PolicyList>
        <li>
          <strong>Account Deletion</strong>
          <br />
          You may delete your account using the account deletion function
          provided in the Services or by contacting us.
          <br />
          When your account is deleted:
          <PolicyHyphenList>
            <li>Your account will be terminated;</li>
            <li>
              Personal Data that can identify you will be deleted or otherwise
              disposed of in accordance with applicable law; and
            </li>
            <li>
              Your previous Personal Data will not be available if you later
              create a new account.
            </li>
          </PolicyHyphenList>
          Certain information may be retained where required or permitted by
          applicable law, including where necessary to comply with legal
          obligations, resolve disputes, prevent fraud or abuse, or enforce our
          agreements.
          <br />
          As described in Section 9, some service data may be{" "}
          <strong>
            irreversibly anonymized before or in connection with account
            deletion
          </strong>{" "}
          and retained indefinitely for research, statistical, analytical, and
          service-improvement purposes. Such anonymized information cannot be
          linked back to you.
        </li>
        <li>
          <strong>Deleting the App</strong>
          <br />
          Deleting the <strong>pregnancy&amp;baby</strong> application from your
          device does not automatically delete your account or Personal Data.
          <br />
          To delete your Personal Data, you must delete your account through the
          Services or submit a deletion request to us.
        </li>
      </PolicyList>

      <PolicySectionTitle>11. Your Privacy Rights</PolicySectionTitle>
      <PolicyText>
        Depending on the laws applicable to you, you may have the right to:
      </PolicyText>
      <PolicyHyphenList>
        <li>Access Personal Data we hold about you</li>
        <li>Obtain a copy of your Personal Data</li>
        <li>Correct inaccurate or incomplete Personal Data</li>
        <li>Request deletion of your Personal Data</li>
        <li>Request restriction of certain processing</li>
        <li>Object to certain processing</li>
        <li>
          Withdraw consent at any time where processing is based on consent
        </li>
        <li>Request portability of your Personal Data where applicable</li>
        <li>Lodge a complaint with an applicable data protection authority</li>
      </PolicyHyphenList>
      <PolicyText>
        Withdrawal of consent does not affect the lawfulness of processing
        carried out before the withdrawal.
      </PolicyText>
      <PolicyText>
        Some requests may be subject to limitations or exceptions under
        applicable law.
      </PolicyText>
      <PolicyText>
        To exercise your privacy rights, contact us at:
        <br />
        <a href="mailto:after10month@connect-i.co.kr">
          after10month@connect-i.co.kr
        </a>
      </PolicyText>
      <PolicyText>
        We may request reasonable information necessary to verify your identity
        before responding to a request.
      </PolicyText>
      <PolicyText>
        We will respond within the period required by applicable law.
      </PolicyText>

      <PolicySectionTitle>12. Security</PolicySectionTitle>
      <PolicyText>
        We implement reasonable technical and organizational measures designed
        to protect Personal Data against unauthorized access, loss, theft,
        misuse, unauthorized disclosure, alteration, or destruction.
      </PolicyText>
      <PolicyText>
        These measures may include encryption of information in transit and at
        rest, access controls, monitoring, vulnerability management, internal
        security procedures, and restrictions on employee access to Personal
        Data.
      </PolicyText>
      <PolicyText>
        Access to Personal Data is limited to personnel who require access for
        legitimate business purposes.
      </PolicyText>
      <PolicyText>
        No method of electronic transmission or storage is completely secure,
        and therefore we cannot guarantee absolute security.
      </PolicyText>
      <PolicyText>
        If a Personal Data breach occurs, we will take appropriate measures and
        provide notifications to affected users or relevant authorities where
        required by applicable law.
      </PolicyText>

      <PolicySectionTitle>
        13. Storage and International Data Transfers
      </PolicySectionTitle>
      <PolicyText>
        Connecti Co., Ltd. is established in the Republic of Korea.
      </PolicyText>
      <PolicyText>
        Our primary application infrastructure and Personal Data storage are
        hosted using{" "}
        <strong>
          Amazon Web Services (AWS), Asia Pacific (Seoul) Region, Republic of
          Korea.
        </strong>
      </PolicyText>
      <PolicyText>
        Because <strong>pregnancy&amp;baby</strong> is available in multiple
        countries and uses global service providers, certain Personal Data or
        technical information may also be processed by, or accessible to,
        service providers located outside your country of residence.
      </PolicyText>
      <PolicyText>
        Such service providers may include, where applicable:
      </PolicyText>
      <PolicyHyphenList>
        <li>Amazon Web Services</li>
        <li>Google</li>
        <li>NHN Cloud</li>
        <li>Sentry</li>
      </PolicyHyphenList>
      <PolicyText>
        The privacy and data protection laws of those countries may differ from
        the laws of your jurisdiction.
      </PolicyText>
      <PolicyText>
        Where required by applicable law, we use appropriate safeguards for
        international transfers of Personal Data, which may include contractual
        safeguards or other legally recognized transfer mechanisms.
      </PolicyText>
      <PolicyText>
        For users in jurisdictions that require specific notice or consent for
        international transfers, we will provide such notice and obtain consent
        where required.
      </PolicyText>

      <PolicySectionTitle>
        14. Children's and Minors' Privacy
      </PolicySectionTitle>
      <PolicyText>
        The Services are intended only for individuals who have reached the{" "}
        <strong>
          minimum age at which they may independently consent to the collection
          and processing of their Personal Data under the laws applicable to
          them.
        </strong>
      </PolicyText>
      <PolicyText>
        If applicable law requires parental or legal guardian consent for an
        individual to use the Services or for us to process that individual's
        Personal Data, that individual may not create or use a{" "}
        <strong>pregnancy&amp;baby</strong> account without the required
        consent.
      </PolicyText>
      <PolicyText>
        We do not knowingly collect Personal Data from individuals who are
        legally unable to provide valid consent and for whom the required
        parental or legal guardian consent has not been obtained.
      </PolicyText>
      <PolicyText>
        If you believe that such an individual has provided Personal Data to us
        in violation of applicable law, please contact us at{" "}
        <a href="mailto:after10month@connect-i.co.kr">
          after10month@connect-i.co.kr
        </a>
        . We will review the request and take appropriate action, including
        deletion where required.
      </PolicyText>
      <PolicyText>
        We may implement age-related restrictions or age-verification measures
        where reasonably necessary or required by applicable law.
      </PolicyText>

      <PolicySectionTitle>
        15. Information About Children Entered by Users
      </PolicySectionTitle>
      <PolicyText>
        Certain child-care features allow a parent or legal guardian to enter
        information relating to their child.
      </PolicyText>
      <PolicyText>This information may include:</PolicyText>
      <PolicyHyphenList>
        <li>Child's name</li>
        <li>Sex</li>
        <li>Date of birth</li>
        <li>Birth measurements</li>
        <li>Growth information</li>
        <li>Developmental information</li>
        <li>Health screening records</li>
        <li>Vaccination records</li>
        <li>Body temperature</li>
        <li>Medication information</li>
        <li>Illness information</li>
        <li>Related photos</li>
      </PolicyHyphenList>
      <PolicyText>
        By entering a child's Personal Data into the Services, you represent
        that you are authorized to provide and manage that information as the
        child's parent, legal guardian, or another person legally authorized to
        do so.
      </PolicyText>
      <PolicyText>
        We process this information only for the purposes described in this
        Privacy Policy and as otherwise permitted by applicable law.
      </PolicyText>

      <PolicySectionTitle>
        16. Third-Party Login Services and External Services
      </PolicySectionTitle>
      <PolicyText>
        If you choose to use Google, Apple to sign in to{" "}
        <strong>pregnancy&amp;baby</strong>, your use of those services is also
        subject to the applicable provider's terms and privacy practices.
      </PolicyText>
      <PolicyText>
        The Services may also contain links to websites, applications, or
        services operated by third parties.
      </PolicyText>
      <PolicyText>
        We are not responsible for the privacy practices of independent third
        parties. We encourage you to review their privacy policies before
        providing Personal Data to them.
      </PolicyText>

      <PolicySectionTitle>17. No Sale of Personal Data</PolicySectionTitle>
      <PolicyText>We do not sell or rent Personal Data.</PolicyText>
      <PolicyText>
        We do not sell health, fertility, pregnancy, or child-related Personal
        Data.
      </PolicyText>

      <PolicySectionTitle>
        18. Changes to This Privacy Policy
      </PolicySectionTitle>
      <PolicyText>We may amend this Privacy Policy to reflect:</PolicyText>
      <PolicyHyphenList>
        <li>Changes to the Services</li>
        <li>Changes in our Personal Data processing practices</li>
        <li>Changes in service providers</li>
        <li>Changes in applicable laws or regulations</li>
      </PolicyHyphenList>
      <PolicyText>
        If a change materially affects your rights or the way we process your
        Personal Data, we will provide appropriate notice before the change
        becomes effective where required by applicable law.
      </PolicyText>
      <PolicyText>
        Where required, we will also obtain renewed consent.
      </PolicyText>
      <PolicyText>
        The effective date and latest revision date will be displayed at the top
        of this Privacy Policy.
      </PolicyText>

      <PolicySectionTitle>19. Data Protection Contact</PolicySectionTitle>
      <PolicyText>
        For questions about this Privacy Policy, the processing of your Personal
        Data, or the exercise of your privacy rights, please contact:
      </PolicyText>
      <PolicyText>
        <strong>Connecti Co., Ltd.</strong>
        <br />
        #56, B2, 176 Yanghwa-ro
        <br />
        Mapo-gu, Seoul, Republic of Korea
        <br />
        <strong>Email:</strong>{" "}
        <a href="mailto:after10month@connect-i.co.kr">
          after10month@connect-i.co.kr
        </a>
      </PolicyText>

      <PolicySectionTitle>
        20. Region-Specific Privacy Rights
      </PolicySectionTitle>
      <PolicyText>
        Privacy and data protection laws vary by country and region.
      </PolicyText>
      <PolicyText>
        If the law of your country or region provides additional privacy rights
        or imposes additional obligations on us, we will process your Personal
        Data and respond to your requests in accordance with those applicable
        requirements.
      </PolicyText>
      <PolicyText>
        Depending on your jurisdiction, you may have additional rights
        concerning:
      </PolicyText>
      <PolicyHyphenList>
        <li>Processing of health or sensitive Personal Data</li>
        <li>Consent and withdrawal of consent</li>
        <li>International transfers of Personal Data</li>
        <li>Automated processing</li>
        <li>
          Access, correction, deletion, restriction, objection, or portability
        </li>
        <li>Complaints to a local supervisory or data protection authority</li>
      </PolicyHyphenList>
      <PolicyText>
        Nothing in this Privacy Policy is intended to limit any rights that
        cannot lawfully be limited under applicable data protection law.
      </PolicyText>
    </PolicyContainer>
  );
};

export default PrivacyEn;
